import AzureAndMapboxGeocodeModule from "./geocode/AzureAndMapboxGeocodeModule";

import type { IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type {ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "./CacheModule";

const $ = global.$;
const es = global.es;

/**
 * @class GeocodeModule
 */
export default class GeocodeModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.getGeoCoder = this.getGeoCoder.bind(this);
        this.getCountries = this.getCountries.bind(this);
        this.getAddress = this.getAddress.bind(this);
        this.geocode = this.geocode.bind(this);
        this.reverseGeocode = this.reverseGeocode.bind(this);
        this.getAddressComponents = this.getAddressComponents.bind(this);
        this.getAddressComponent = this.getAddressComponent.bind(this);
    }

    /**
     * 
     * @returns 
     */
    getGeoCoder() {
        return new AzureAndMapboxGeocodeModule();
    }

    /**
     * Get the resource from the server
     * 
     * @returns a jquery deferred object resolved when the countries are loaded
     */
    getCountries(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/countries";
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    
    private getAddress(addressAttrs: [], addressValues: string[]) {
        const isZipPromise = Promise.all(addressAttrs.map((a) => es.api.heuristics.isZip(a.title)));
        const isPostCodePromise = Promise.all(addressAttrs.map((a) => es.api.heuristics.isPostCode(a.title)));
        
        return $.when(isZipPromise, isPostCodePromise).then((isZip: boolean[], isPostCode: boolean[]) => {
            let address = '';
            
            addressValues.forEach((addressValue, i) => {
                addressValue = addressValue || ''; // just in case there are null values in there
                
                addressValue.split(',').forEach((addrPart) => {
                    addrPart = addrPart.trim();
                    if (addrPart == '') {
                        return;
                    }
                    
                    if (address.length > 0) {
                        address += ',';
                    }
                    
                    if (isZip[i] && /^\d{4}$/.test(addrPart)) {
                        address += '0';
                    } else if (isZip[i] && /^\d{3}$/.test(addrPart)) {
                        address += '00';
                    } else if (isPostCode[i] && /^\d{3}$/.test(addrPart)) {
                        address += '0';
                    }
                    
                    if (isZip[i] || isPostCode[i]) {
                        var a = addrPart.split('-');
                        if (a.length == 2 && a[0].length == 5 && a[1].length == 4 && /^\d+$/.test(a[0]) && /^\d+$/.test(a[1])) {
                            es.log.info('Removing second part of ZIP/PostCode [' + addrPart + "] -> [" + a[0] + "]");
                            
                            addrPart = a[0];
                        }
                    }
                    
                    address += addrPart;
                });
            });
            address = address.toUpperCase();
            
            return address;
        });
    }

    /**
     * get the location by address detail
     * 
     * @param addressAttrs
     * @param addressValues the address
     * @param countryCode
     * 
     * @returns the location which the format is [lng, lat].
     */
    geocode(addressAttrs, addressValues, countryCode) {
        if (!(addressValues instanceof Array)) { // this implies !(addressHeadings instanceof Array)
            addressAttrs = [addressAttrs];
            addressValues = [addressValues];
        }

        // ensure vals to strings
        const strVals = addressValues.map(value => {
            if (value === null || value === undefined) {
                return '';
            }
            return String(value);
        });
        
        return this.getAddress(addressAttrs, strVals).then((address) => {
            return this.getGeoCoder().geocode(address, countryCode);
        });
    }
    
    /**
     * Get the address information by location detail.
     */
    reverseGeocode(lonLat: number[]) {
        var dfd = $.Deferred();

        this.getGeoCoder().reverseGeocode(lonLat).done((addressStr, newAddress) => {
            dfd.resolve(addressStr, newAddress);
        });
        
        return dfd.promise();
    }

    /**
     * @returns
     */
    getAddressComponents() {
        return this.getGeoCoder().addressComponents;
    };

    /**
     * 
     * @param component 
     * @returns 
     */
    getAddressComponent(component: string) {
        return this.getGeoCoder().addressComponents[component];
    }
}
